/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import deferToClientSideRender from 'js/lib/deferToClientSideRender';

import type { Theme } from '@coursera/cds-core';
import { Grid, Typography } from '@coursera/cds-core';

import type { EnterpriseArticle, Maybe, Sys } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import { createCanonicalEntArticleLink } from 'bundles/ent-website/articles/utils/entSeoUtils';
import RelatedArticlesCard from 'bundles/shared-articles/components/RelatedArticlesCard';

import _t from 'i18n!nls/ent-website';

type RelatedArticleSys = { sys: Pick<Sys, 'publishedAt'> };

type RelatedArticle = Pick<EnterpriseArticle, 'title' | 'slug' | 'localizedCountryCode' | 'localizedLanguageCode'> &
  RelatedArticleSys;

type Props = {
  articles?: Maybe<RelatedArticle>[];
};

const styles = {
  fullWidthContainer: (theme: Theme) => css`
    margin: 32px 0;
    padding: ${theme.spacing(16, 0)};
    width: 100%;
    position: relative;
  `,
  relatedArticlesContainer: (theme: Theme) => css`
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    max-width: 100%;
    margin: 0 auto;
    padding: ${theme.spacing(24, 32)};

    ul {
      list-style-type: none;
      padding: ${theme.spacing(0)};

      ${theme.breakpoints.down('xs')} {
        li:nth-of-type(n + 4) {
          display: none;
        }
      }
    }
  `,
  sessionTitle: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(16)};
  `,
};

export const EnterpriseRelatedArticles = ({ articles }: Props) => {
  if (!articles || articles.length === 0) return null;

  return (
    <div css={styles.fullWidthContainer}>
      <div css={styles.relatedArticlesContainer}>
        <Typography css={styles.sessionTitle} variant="h2semibold">
          {_t('Keep reading')}
        </Typography>
        <Grid container spacing={24} component="ul">
          {articles.map((article) => {
            return (
              article?.slug &&
              article?.title && (
                <RelatedArticlesCard
                  title={article.title}
                  publishedDate={article.sys.publishedAt}
                  key={article.slug}
                  articleUrl={`${createCanonicalEntArticleLink(article?.slug)}?trk_ref=relatedArticlesCard`}
                />
              )
            );
          })}
        </Grid>
      </div>
    </div>
  );
};

export default deferToClientSideRender(EnterpriseRelatedArticles);
